import { ColumnHelper } from '@tanstack/react-table';

import { NumberFormatProps } from '../NumberCell';
import { booleanCol, dateCol, moneyCol, numberCol, textCol } from './colDef';

/**
 * Builds a set of typed column accessors for a sub-object of a row.
 *
 * Avoids repeating `columnHelper.accessor((row) => row.base.X, textCol<TData>({ id: 'X' }))`
 * for every column. Pick a sub-object via `select`, then call `.text('fieldName')` etc.
 *
 * Function-form accessors are used (not `'base.X'` strings) so tanstack-table's
 * dot-path walker doesn't emit "deeply nested key returned undefined" warnings
 * for optional fields that the backend omits.
 *
 * Example:
 *   const columnHelper = createColumnHelper<Portfolio>();
 *   const base = makeFieldAccessors(columnHelper, (row) => row.base);
 *   const cols = [base.text('pId'), base.bool('isCashOnly'), base.num('rate', { fractionDigits: 2 })];
 */
export const makeFieldAccessors = <TData, TSub extends object>(
    columnHelper: ColumnHelper<TData>,
    select: (row: TData) => TSub | undefined,
) => ({
    text: <K extends keyof TSub & string>(key: K, header?: string) =>
        columnHelper.accessor((row) => select(row)?.[key], textCol<TData>({ id: key, header })),
    bool: <K extends keyof TSub & string>(key: K, header?: string) =>
        columnHelper.accessor((row) => select(row)?.[key], booleanCol<TData>({ id: key, header })),
    date: <K extends keyof TSub & string>(key: K, header?: string) =>
        columnHelper.accessor((row) => select(row)?.[key], dateCol<TData>({ id: key, header })),
    money: <K extends keyof TSub & string>(key: K, header?: string) =>
        columnHelper.accessor((row) => select(row)?.[key], moneyCol<TData>({ id: key, header })),
    num: <K extends keyof TSub & string>(key: K, format?: NumberFormatProps, header?: string) =>
        columnHelper.accessor((row) => select(row)?.[key], numberCol<TData>({ id: key, format, header })),
});
